import {
  Box,
  Drawer,
  IconButton,
  styled,
  Theme,
  Typography,
} from '@mui/material';

const hairline = (theme: Theme) =>
  `1px solid ${theme.palette.contrast.opacity8}`;

export const StyledDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== '$width' && prop !== '$flat',
})<{ $width: string; $flat?: boolean }>(({ theme, $width, $flat }) => ({
  zIndex: 1290,

  '& .MuiDrawer-paper': {
    width: `min(${$width}, 100vw)`,
    overflow: 'hidden',
    overscrollBehavior: 'contain',
    backgroundColor: theme.palette.main.white,
    borderLeft: hairline(theme),
    boxShadow: '0px 16px 40px rgba(6, 14, 21, 0.12)',

    [theme.breakpoints.down('md')]: {
      width: '100vw',
      borderLeft: 'none',
    },

    ...($flat && {
      [theme.breakpoints.up('lg')]: {
        boxShadow: 'none',
      },
    }),
  },
}));

export const DrawerHeader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3, 0),
}));

export const DrawerTitle = styled(Typography)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  color: theme.palette.main.black,
}));

export const DrawerCloseButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  fontSize: 22,
  color: theme.palette.main.black,

  '&:hover': { backgroundColor: theme.palette.main.grey100 },
}));

export const DrawerSubheader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3, 2.5),
}));

export const DrawerContent = styled(Box, {
  shouldForwardProp: prop => prop !== '$grey' && prop !== '$hideScrollbar',
})<{ $grey: boolean; $hideScrollbar: boolean }>(
  ({ theme, $grey, $hideScrollbar }) => ({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    padding: theme.spacing(0, 3, 6),

    '& > * > *:last-child': {
      marginBottom: 0,
    },

    ...($grey && {
      padding: theme.spacing(3, 3, 6),
      backgroundColor: theme.palette.main.grey50,
    }),

    ...($hideScrollbar && {
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }),
  })
);

export const DrawerFooter = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 3),
  borderTop: hairline(theme),
  backgroundColor: theme.palette.main.white,
}));

export const EmbeddedFormWrapper = styled(Box)({
  '& > div': {
    border: 'none',
    borderRadius: 0,
    margin: 0,
    padding: 0,
  },
  '& > div > div:first-of-type': {
    display: 'none',
  },
});
